import { isSelectionActive, resolveProgramCode, resolveProgramName } from './dataNormalizer';
import {
  isLegacyPresenceLifecycleProjection,
  resolveLifecycleEvidenceValue,
} from './enrollmentEvidence';

const MISSING_FROM_API_STATUS = 'MISSING_FROM_API';
const MISSING_PROTECTED_SCALAR_FIELDS = new Set([
  'enrollmentStatusCd',
  'enrollmentStatus',
  'programStatusCd',
  'programStatus',
  'programSelectionStatusCd',
  'productSelectionStatusCd',
  'cancelDate',
  'cancelDt',
  'cancelledAt',
  'scheduledCancelDate',
  'syntheticCancellation',
  'syntheticCancellationState',
]);
const API_PRESENCE_FIELDS = [
  'missingFromApi',
  'apiPresenceDerived',
  'apiPresenceLegacyCooling',
  'apiPresenceStatus',
  'missingSince',
  'missingConfirmedAt',
  'apiLastSeenAt',
];
const WEBSITE_OWNERSHIP_FIELDS = [
  'websiteOwnershipStatus',
  'dateFirstSwitchedWebsiteProvider',
  'websiteOwnershipLastCheckedAt',
];

export const normalizeEnrollmentBac = (row) => {
  const value = row?.bac ?? row?.dealerCd ?? row?.dealerId ?? row?.dealerCode ?? row?.dealerNumber ?? null;
  return value === null || value === undefined ? '' : String(value).trim();
};

export const mergeEnrollmentRow = (prev, payload) => {
  if (!payload) return prev;
  const next = { ...prev };
  const lifecycleStatus = resolveLifecycleEvidenceValue(payload);
  const enrollmentStatus = lifecycleStatus;
  const programStatus = lifecycleStatus;
  const enrollmentDate = payload.enrolledDt ?? payload.enrollmentDate ?? payload.enrollmentDt;
  const optOutDate = payload.optOutDt ?? payload.optOutDate;
  const updateDate = payload.lastUpdateDt ?? payload.updateDate;
  const deriveIsEnrolled = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    const normalized = String(value).trim().toUpperCase();
    if (!normalized) return null;
    if (['Y', 'YES', 'TRUE', '1', 'ENROLLED', 'LIVE', 'ACTIVE'].includes(normalized)) return true;
    if (['N', 'NO', 'FALSE', '0', 'OPTED OUT', 'OPTOUT', 'CANCELLED', 'CANCELED', 'TERMINATED'].includes(normalized)) {
      return false;
    }
    return null;
  };
  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(payload, key);
  const normalizedEnrollmentStatus = enrollmentStatus === null || enrollmentStatus === undefined
    ? ''
    : String(enrollmentStatus).trim().replace(/[\s-]+/g, '_').toUpperCase();
  const isApiPresenceProjection = isLegacyPresenceLifecycleProjection(payload);

  // Scalar fields keep their previous value when a partial payload omits them. Explicit
  // active-state updates clear synthetic-cancel metadata below so stale cancel dates do not linger.
  const candidateUpdates = [
    ['enrollmentStatusCd', enrollmentStatus],
    ['enrollmentStatus', enrollmentStatus],
    ['programStatusCd', programStatus],
    ['programStatus', programStatus],
    ['programConsentStatusCd', payload.programConsentStatusCd],
    ['dataConsentStatusCd', payload.dataConsentStatusCd],
    ['programSelectionStatusCd', payload.programSelectionStatusCd],
    ['productSelectionStatusCd', payload.productSelectionStatusCd],
    ['enrollmentDate', enrollmentDate],
    ['enrolledDt', enrollmentDate],
    ['dealerName', payload.dealerName],
    ['bac', payload.bac ?? payload.dealerCd],
    ['dealerCd', payload.dealerCd ?? payload.bac],
    ['isTerminated', payload.isTerminated],
    ['isTerminatedFlag', payload.isTerminatedFlag],
    ['terminatedFlag', payload.terminatedFlag],
    ['isTerminatedDate', payload.isTerminatedDate],
    ['cancelDate', payload.cancelDate],
    ['cancelDt', payload.cancelDt],
    ['cancelledAt', payload.cancelledAt],
    ['scheduledCancelDate', payload.scheduledCancelDate],
    ['syntheticCancellation', payload.syntheticCancellation],
    ['syntheticCancellationState', payload.syntheticCancellationState],
  ];

  candidateUpdates.forEach(([key, value]) => {
    if (
      isApiPresenceProjection &&
      MISSING_PROTECTED_SCALAR_FIELDS.has(key) &&
      ((['enrollmentStatusCd', 'enrollmentStatus', 'programStatusCd', 'programStatus'].includes(key)) ||
        (next[key] !== null && next[key] !== undefined))
    ) {
      return;
    }
    if (value !== null && value !== undefined) {
      next[key] = value;
    }
  });

  API_PRESENCE_FIELDS.forEach((field) => {
    if (hasOwn(field) && payload[field] !== undefined) next[field] = payload[field];
  });
  WEBSITE_OWNERSHIP_FIELDS.forEach((field) => {
    if (hasOwn(field)) next[field] = payload[field] ?? null;
  });

  if (isApiPresenceProjection) {
    if (!hasOwn('apiPresenceStatus')) next.apiPresenceStatus = normalizedEnrollmentStatus;
    if (!hasOwn('apiPresenceDerived')) next.apiPresenceDerived = true;
    if (!hasOwn('missingFromApi')) next.missingFromApi = normalizedEnrollmentStatus === MISSING_FROM_API_STATUS;
  }

  if (String(next.apiPresenceStatus || '').trim().toUpperCase() === 'PRESENT') {
    next.missingFromApi = false;
    next.missingSince = null;
    next.missingConfirmedAt = null;
  }

  if (optOutDate !== null && optOutDate !== undefined) {
    next.optOutDate = optOutDate;
    next.optOutDt = optOutDate;
  }

  if (updateDate !== null && updateDate !== undefined) {
    next.lastUpdateDate = updateDate;
    next.lastUpdateDt = updateDate;
  }

  const nextIsEnrolled = isApiPresenceProjection
    ? null
    : deriveIsEnrolled(payload.isEnrolled) ??
      (enrollmentStatus ? String(enrollmentStatus).trim().toUpperCase() === 'ENROLLED' : null);
  if (nextIsEnrolled !== null) {
    next.isEnrolled = nextIsEnrolled;
  }

  const shouldClearSyntheticCancel =
    !isApiPresenceProjection &&
    ((payload.syntheticCancellation === false && normalizedEnrollmentStatus !== 'COOLING') ||
      (hasOwn('syntheticCancellationState') && payload.syntheticCancellationState === null) ||
      (normalizedEnrollmentStatus &&
        !['COOLING', 'CANCELLED', 'CANCELED', 'TERMINATED'].includes(normalizedEnrollmentStatus)));
  if (shouldClearSyntheticCancel) {
    next.syntheticCancellation = false;
    next.syntheticCancellationState = null;
    if (!isApiPresenceProjection) {
      next.cancelDate = null;
      next.cancelDt = null;
      next.cancelledAt = null;
    }
  }

  if (normalizedEnrollmentStatus && !isApiPresenceProjection && !hasOwn('apiPresenceStatus')) {
    next.missingFromApi = false;
    next.apiPresenceDerived = false;
    next.apiPresenceStatus = null;
    next.missingSince = null;
    next.missingConfirmedAt = null;
    next.apiLastSeenAt = null;
  }

  if (Array.isArray(payload.vendors) && (!isApiPresenceProjection || !Array.isArray(next.vendors))) {
    next.vendors = payload.vendors;
  }

  if (
    Array.isArray(payload.productSelections) &&
    (!isApiPresenceProjection || !Array.isArray(next.productSelections))
  ) {
    next.productSelections = payload.productSelections;
    next.hasLiveSelections = payload.productSelections.some(isSelectionActive);
  }

  const resolvedProgramName =
    resolveProgramName(payload.programCode) ||
    resolveProgramName(payload.program) ||
    resolveProgramName(payload.programName) ||
    resolveProgramName(payload.programLabel) ||
    null;
  const resolvedProgramCode =
    resolveProgramCode(payload.programCode) ||
    resolveProgramCode(payload.program) ||
    resolveProgramCode(payload.programName) ||
    resolveProgramCode(payload.programLabel) ||
    null;

  next.programCode = resolvedProgramCode || payload.programCode || payload.program || next.programCode;
  next.programName = resolvedProgramName || next.programName;
  next.programLabel = resolvedProgramName || next.programLabel;
  next._original = payload;
  return next;
};
